import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, output, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

import { PartnerDirectoryService, PartnerLocationOption, PartnerOption, PartnerServiceType } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, combineLatest, debounceTime, of, switchMap } from 'rxjs';

import { MatIcon } from '@angular/material/icon';

import { CalloutComponent } from '../components/common/callout.component';
import { ControlDirective } from '../components/common/control.directive';
import { OptionListComponent } from '../components/common/option-list.component';
import { OptionRowDirective } from '../components/common/option-row.directive';
import { OVERLAY_POSITIONS, SEARCH_DEBOUNCE_MS, SearchFieldComponent, fieldWidth } from '../components/common/search-field.component';
import { SEGMENTED_GROUP_CLASS, ToggleDirective } from '../components/common/toggle.directive';
import { LocationMapComponent, LocationMarker, MAP_VIEWS, MapView } from './location-map.component';
import { PartnerPickerComponent } from './partner-picker.component';

/** Where the care happens and who delivers it — the part of a booking that names a place. */
export interface PlaceChoice {
  partnerId: string;
  partnerName: string;
  /** Off means nothing reaches the partner's system, so the order number is ours to record. */
  integrationEnabled: boolean;
  locationId?: string;
  locationAddress?: string;
  /** Typed as the partner said it: a visit arranged by phone is often with someone no directory holds. */
  doctorName?: string;
}

/**
 * Partner, then one of its locations, then for a visit the doctor's name.
 *
 * One component rather than three: a location belongs to a partner, and changing the partner
 * invalidates the location under it, which is a rule that has to live in one place.
 *
 * The branch is answered as an address or on a map, over the same narrowed set either way.
 *
 * Each field keeps what was typed apart from what was answered: the text in the box narrows the
 * list and nothing else, and the answer is shown in its place once the box is empty again. Writing
 * the answer back into the box would make it the next search term — and a branch is labelled with
 * its name and address joined, which is a string the directory holds no record of, so re-opening
 * the list found nothing and the branch just chosen could not be chosen again.
 */
@Component({
  selector: 'guarantees-place-picker',
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatIcon,
    TranslatePipe,
    LocationMapComponent,
    OptionListComponent,
    OptionRowDirective,
    PartnerPickerComponent,
    SearchFieldComponent,
    CalloutComponent,
    ControlDirective,
    ToggleDirective,
  ],
  templateUrl: './place-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlacePickerComponent implements OnInit {
  protected readonly segmentedGroup = SEGMENTED_GROUP_CLASS;

  service = input.required<PartnerServiceType>();
  /** Only an appointment is delivered by a named person; an order is placed with the partner. */
  withDoctor = input<boolean>(false);
  /** What was answered before, so stepping back and forward again shows the answer rather than blanks. */
  initial = input<PlaceChoice | undefined>(undefined);

  changed = output<PlaceChoice | undefined>();

  private readonly directory = inject(PartnerDirectoryService);

  protected readonly locationTerm = signal('');
  protected readonly doctorName = signal('');

  protected readonly locations = signal<PartnerLocationOption[]>([]);

  protected readonly partner = signal<PartnerOption | undefined>(undefined);
  protected readonly location = signal<PartnerLocationOption | undefined>(undefined);

  protected readonly loadingLocations = signal(false);

  protected readonly listOpen = signal(false);

  protected readonly view = signal<MapView>('list');
  protected readonly views = MAP_VIEWS;

  protected readonly overlayPositions = OVERLAY_POSITIONS;

  /**
   * Measured when the list opens rather than read off the element in the template: a field inside a
   * dialog still being laid out measures zero, which the overlay reads as no width at all.
   */
  protected readonly panelWidth = signal(0);

  private readonly locationOrigin = viewChild.required('locationOrigin', { read: CdkOverlayOrigin });

  /** Nothing to type into until a partner is named: the branches are that partner's. */
  protected readonly partnerChosen = computed(() => !!this.partner());

  // What the field shows when nothing is being typed into it. The answer stands in the field's
  // placeholder rather than in its value, so it survives a search that would not match it.
  protected readonly locationDisplay = computed(() => {
    const location = this.location();

    return location ? this.locationLabel(location) : '';
  });

  /** A branch the directory holds no point for cannot be pinned, so the map is only what it can show. */
  protected readonly markers = computed<LocationMarker[]>(() =>
    this.locations()
      .filter(location => location.latitude !== undefined && location.longitude !== undefined)
      .map(location => ({ id: location.id, title: this.locationLabel(location), latitude: location.latitude!, longitude: location.longitude! }))
  );

  /**
   * Said rather than shown empty: a partner whose branches carry no coordinates has no map. Not
   * while a read is out, though — an empty list mid-fetch is not yet an answer about coordinates,
   * and the map standing there with no pins on it says the same thing without claiming it.
   */
  protected readonly mapEmpty = computed(() => !this.loadingLocations() && this.markers().length === 0);

  constructor() {
    // The branches belong to the chosen partner, so the partner is part of what drives them — the
    // same pairing the partner list makes with the service. A partner taken is a fetch on its own
    // account, rather than something a second path has to remember to ask for.
    combineLatest([toObservable(this.partner), toObservable(this.locationTerm)])
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        switchMap(([partner, term]) => this.fetchLocations(partner?.id, term)),
        takeUntilDestroyed()
      )
      .subscribe(locations => {
        this.locations.set(locations);
        this.loadingLocations.set(false);
      });
  }

  /** Read here rather than in the constructor: an input has no value until Angular has bound it. */
  ngOnInit(): void {
    const initial = this.initial();
    if (!initial) {
      return;
    }

    this.partner.set({ id: initial.partnerId, name: initial.partnerName, integrationEnabled: initial.integrationEnabled });

    if (initial.locationId) {
      // The label is what was shown before; its parts are not held apart once they have been joined.
      this.location.set({ id: initial.locationId, name: '', address: initial.locationAddress ?? '' });
    }

    this.doctorName.set(initial.doctorName ?? '');
  }

  private fetchLocations(partnerId: string | undefined, term: string) {
    if (!partnerId) {
      return of<PartnerLocationOption[]>([]);
    }

    this.loadingLocations.set(true);

    return this.directory.searchLocations(partnerId, this.service(), term).pipe(catchError(() => of<PartnerLocationOption[]>([])));
  }

  protected choosePartner(partner: PartnerOption): void {
    this.partner.set(partner);
    this.listOpen.set(false);

    // The branch belongs to the partner that has just been replaced.
    this.location.set(undefined);
    this.locationTerm.set('');
    this.locations.set([]);

    // Marked in flight here rather than where the read starts: the read waits out the debounce, and
    // the emptied list would read as an answer for as long as it waited.
    this.loadingLocations.set(true);

    this.emit();
  }

  protected setView(view: MapView): void {
    this.view.set(view);
    this.listOpen.set(false);
  }

  protected chooseFromMap(locationId: string): void {
    const location = this.locations().find(option => option.id === locationId);
    if (location) {
      this.chooseLocation(location);
    }
  }

  protected chooseLocation(location: PartnerLocationOption): void {
    this.location.set(location);
    this.locationTerm.set('');
    this.listOpen.set(false);
    this.emit();
  }

  /**
   * A chain names most of its branches after itself, so the name is only worth reading where it says
   * something the partner has not already said.
   */
  protected locationLabel(location: PartnerLocationOption): string {
    return this.namesBranch(location) ? [location.name, location.address].filter(Boolean).join(' — ') : location.address;
  }

  protected namesBranch(location: PartnerLocationOption): boolean {
    return !!location.name && location.name !== this.partner()?.name;
  }

  protected typeDoctor(value: string): void {
    this.doctorName.set(value);
    this.emit();
  }

  /** On a map the typing narrows the pins, so a list over them would cover what it narrowed. */
  protected typeLocation(value: string): void {
    this.openLocationList();
    this.locationTerm.set(value);
  }

  protected openLocationList(): void {
    if (this.view() === 'list') {
      this.panelWidth.set(fieldWidth(this.locationOrigin()));
      this.listOpen.set(true);
    }
  }

  /**
   * The box holds the query while the list is open, and the answer once it is not: a term left
   * standing behind a closed list narrows nothing and covers the branch actually chosen.
   */
  protected closeLocation(): void {
    this.locationTerm.set('');
    this.close();
  }

  protected close(): void {
    this.listOpen.set(false);
  }

  private emit(): void {
    const partner = this.partner();
    if (!partner) {
      this.changed.emit(undefined);

      return;
    }

    const location = this.location();

    this.changed.emit({
      partnerId: partner.id,
      partnerName: partner.name,
      integrationEnabled: partner.integrationEnabled,
      locationId: location?.id,
      locationAddress: location ? this.locationLabel(location) : undefined,
      doctorName: (this.withDoctor() && this.doctorName().trim()) || undefined,
    });
  }
}
